export const PageHeader = {
  flow: 'x',
  flexWrap: 'wrap',
  align: 'center space-between',
  padding: 'Z A',
  // BORDERLESS (DESIGN_LANGUAGE.md §2.5/§7.2 — the PageBar contract, which
  // this component IS for in-shell surfaces; ChatPaneHeader proved it by
  // zeroing the old hairline): a sticky bar separates from the content that
  // scrolls beneath it by an opaque stage fill, never a stroke. This was the
  // last hairline in shell chrome — 31 consumers converge on this line.
  // THE SHEET COLOUR (Nika 2026-09-24, Q2 (B) — the sheet is flat): the bar
  // paints the sheet pair under it — gray2 dark / gray16 light, the pair of
  // AppMain.js and of an app route's content element (app.js); the three
  // sites move together — so the bar,
  // the sheet and the frame are one colour in both schemes, and only the
  // cards and the rail step off them. It painted the retired `common-card`
  // theme: white in light over the gray16 frame (1.04:1, a white bar over a
  // grey sheet). `color: 'title'` is the ink.
  background: 'gray2',
  '@light': { background: 'gray16' },
  minHeight: 'D',
  gap: 'A',
  color: 'title',
  position: 'sticky',
  top: '0',
  zIndex: '20',
  boxSizing: 'border-box',
  minWidth: '0',
  maxWidth: '100%',

  '@mobileL': {
    flow: 'y',
    align: 'stretch flex-start',
    minHeight: 'auto',
    gap: 'Z',
    padding: 'Z A'
  },
  '@appMobile': {
    flow: 'y',
    align: 'stretch flex-start',
    minHeight: 'auto',
    gap: 'Z',
    padding: 'Z A'
  },

  LeftGroup: {
    flow: 'x',
    align: 'center left',
    gap: 'Z1',
    flex: '1 1 auto',
    flexWrap: 'wrap',
    minWidth: '0',
    maxWidth: '100%',
    ':empty': { display: 'none' },
    '@mobileL': {
      width: '100%',
      maxWidth: '100%'
    },
    '@appMobile': {
      width: '100%',
      maxWidth: '100%'
    }
  },

  CenterGroup: {
    // PageSlot can be narrow while the viewport is wide. Keeping all three
    // groups in flow lets their actual content decide when to wrap, instead
    // of centering tabs over the title and actions at a viewport breakpoint.
    flow: 'x',
    align: 'center',
    position: 'static',
    flex: '0 1 auto',
    minWidth: '0',
    maxWidth: '100%',
    overflowX: 'auto',
    overscrollBehaviorX: 'contain',
    ':empty': { display: 'none' },
    '@mobileL': {
      position: 'relative',
      left: 'auto',
      top: 'auto',
      transform: 'none',
      width: '100%',
      maxWidth: '100%',
      overflowX: 'auto',
      justifyContent: 'flex-start'
    },
    '@appMobile': {
      width: '100%',
      maxWidth: '100%',
      justifyContent: 'flex-start'
    }
  },

  RightGroup: {
    flow: 'x',
    align: 'center right',
    gap: 'Z1',
    flex: '0 1 auto',
    flexWrap: 'wrap',
    minWidth: '0',
    maxWidth: '100%',
    marginLeft: 'auto',
    ':empty': { display: 'none' },
    '@mobileL': {
      width: '100%',
      maxWidth: '100%',
      justifyContent: 'flex-start',
      flexWrap: 'wrap'
    },
    '@appMobile': {
      width: '100%',
      maxWidth: '100%',
      justifyContent: 'flex-start'
    }
  }
}

// The page title inside a PageHeader — `Title: { extends: 'PageHeaderTitle', text }`.
//
// One title per host (design-direction.md §3). The operator pages, /data and
// /admin/* carried their title as a bare `Title: { text }`: an unstyled `div`.
// So at its dedicated URL none of those pages had an `h1` (live walk
// 2026-09-21), and a literal `tag: 'h1'` would have put a SECOND `h1` into
// every PageSlot pane, under the bar that already is one. `pageTitleTag`
// answers `h1` alone and `h2` under a pane bar.
//
// THE LOOK: 20px bold (`B` / 700), the page-title step /people and the
// analytics lists already use. Until 2026-09-22 this pinned the bare `div`'s
// 17px regular — the size and weight of body text — so on /admin the page's
// `h1` sat ABOVE a bold 20px section heading ("Active · 9") and read as the
// weaker of the two: the hierarchy was upside down (seen live; brand language:
// a title is `title` ink at 700, and design-direction.md §7: titles establish
// location). A heading tag's own 2em and margins are still reset here.
// The `|| 'h1'` matters: a bundle that never registered the
// shared question answers undefined, and the framework then infers the tag
// from the KEY — `Title` is a structural tag it refuses, so the node would be
// a `div` again. Such a bundle has no PageSlot either, so `h1` is right there.
// A title that REPEATS the pane bar also takes
// `hide: (el, s) => el.call('paneTitleBarShown', s)` at its page —
// __tests__/paneTitleYield.test.js holds that to the language bundles.
export const PageHeaderTitle = {
  tag: (el, s) => el.call('pageTitleTag', s) || 'h1',
  margin: '0',
  minWidth: '0',
  fontSize: 'B',
  fontWeight: '700'
}
